/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/entity-relation
 * @desc Column Definition for metadata entity relation
 */

import type { ValueGetterParams } from 'ag-grid-community';
import { EntityLink } from 'labstep-web/components/Entity/Link';
import { EntityRelationSearchSelect } from 'labstep-web/components/EntityRelation/SearchSelect';
import { ICONS } from 'labstep-web/constants/icons';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { EntityUpdateContainer } from 'labstep-web/containers/Entity/Update';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { Flex } from 'labstep-web/core/Flex';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { EntityRelation } from 'labstep-web/models/entity-relation.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';

export const getMetadataEntityRelationColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  entityName?: string,
): NewIndexColDef => {
  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.type.entity_relation,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
      },
    },
    editable: true,
    // Value getter returns array of counterpart entity names (preview first, fallback legacy)
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return '';
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      ) as Metadata | null;
      if (!metadata) {
        return '';
      }

      // Preview from backend (virtual property)
      const preview: EntityRelation[] =
        (metadata as any).entity_relation_preview || [];

      const resourceRow = params.node.data; // current row entity (Resource / ResourceItem etc.)
      const resourceId = resourceRow?.id;

      const buildNames = (relations: EntityRelation[]) =>
        relations
          .filter((er) => er.target_resource || er.source_resource)
          .map((er) => {
            const isSource =
              er.source_resource &&
              er.source_resource.id === resourceId;
            const counterpart = isSource
              ? er.target_resource
              : er.source_resource;
            return counterpart?.name;
          })
          .filter(Boolean);

      if (preview.length) {
        return buildNames(preview);
      }

      return '';
    },
    // Custom renderer to show linked counterpart entities with icons
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return '';
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      ) as Metadata | null;

      // Show placeholder to create metadata if it lacks an id (template only)
      if (!metadata) {
        const parentEntity =
          parentEntityClass !== params.node.data.entityName
            ? params.node.data[parentEntityClass]
            : params.node.data;
        return (
          <EntityCreateContainer
            entityName={Metadata.entityName}
            parentName={MetadataThread.entityName}
            parentId={parentEntity.metadata_thread.idAttr}
          >
            {({ create }) => (
              <TextPlaceholder
                onClick={() =>
                  create(
                    {
                      type,
                      label: metadataLabel,
                      metadata_thread_id:
                        parentEntity.metadata_thread.idAttr,
                    },
                    {},
                  )
                }
                data-cy={`grid/NewIndex/coldefs/metadata/entityRelation:Create-${metadataLabel}`}
              >
                Load entity relations
              </TextPlaceholder>
            )}
          </EntityCreateContainer>
        );
      }

      const relations: EntityRelation[] =
        (metadata as any).entity_relation_preview || [];
      const resourceRow: any = params.node.data;
      const resourceId = resourceRow?.id;

      if (!relations.length) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridNewIndexService.getEditable(params)}
            children="Select"
          />
        );
      }
      return (
        <Flex
          spacing="gap"
          vAlignContent="center"
          data-cy={`grid/NewIndex/coldefs/metadata/entityRelation:CellRenderer-${resourceRow?.name}`}
        >
          {relations.map((er: EntityRelation) => {
            const isSource =
              er.source_resource &&
              er.source_resource.id === resourceId;
            const counterpart = isSource
              ? er.target_resource
              : er.source_resource;
            if (!counterpart?.guid) {
              return null;
            }
            return (
              <div key={`${er.guid}-${counterpart.guid}`}>
                <EntityLink entity={counterpart} showIcon />
              </div>
            );
          })}
        </Flex>
      );
    },
    cellEditorPopup: true,
    cellEditorPopupPosition: 'under',
    cellEditor: (params: any) => {
      if (!params.node) {
        return '';
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      ) as Metadata | null;
      if (!metadata || !metadata.field_definition) {
        return '';
      }
      return (
        <ParamsStateContextProvider value={{}}>
          <Flex width={'100%'}>
            <EntityUpdateContainer
              entityName={EntityRelation.entityName}
              batch
            >
              {({ update }): React.ReactElement => (
                <EntityCreateContainer
                  entityName={EntityRelation.entityName}
                  batch
                >
                  {({ create }): React.ReactElement => (
                    <EntityRelationSearchSelect
                      metadata={metadata}
                      sourceEntity={params.node.data}
                      createEntityRelation={create}
                      updateEntityRelation={update}
                      stopEdit={() => params.api.stopEditing()}
                    />
                  )}
                </EntityCreateContainer>
              )}
            </EntityUpdateContainer>
          </Flex>
        </ParamsStateContextProvider>
      );
    },
    // Keep existing keyboard suppression behaviour for consistency
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => null,
  };
};
